import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class EmailCreateService {
    constructor(private datatask: DataTaskService) {}
    /**
     * Retrieves the page get for email create.
     *
     * @datatask 898
     * @datataskName sp_ws_p2_w_emailcreate_get
     * @param params arguments for the email create to retrieve.
     * @returns a promise containing the page get for email create.
     */
    public async getEmailCreate(params: EmailCreateParams): Promise<EmailCreateGet> {
        return (await this.datatask.Post(898, params))[0];
    }

    /**
     * Retrieves the stored values of the last unsent email.
     *
     * @datatask 1121
     * @datataskName sp_ws_p2_w_emailcreate_get_before
     * @returns a promise containing the last unsent email.
     */
    public async getEmailCreateBefore(): Promise<EmailCreateBeforeGet> {
        return (await this.datatask.Post(1121))[0];
    }

    /**
     * @datatask 3331
     * @datataskName sp_ws_p2_emailcreate_send_internal
     * @param params config of the email to send.
     * @returns a promise containing data to send by frontend or error message.
     */
    public async sendEmail(params: EmailCreateSendParams): Promise<ErrorMessageResponse[] | (ErrorMessageResponse & { mailinfo: EmailCreateClientSend[] })> {
        return await this.datatask.Post(3331, params);
    }

    /**
     * Searches for email recipients.
     *
     * @datatask 1120
     * @datataskName sp_ws_p2_w_emailadd_search_list
     * @param params parameters to use for the search.
     * @returns promise containing list of items representing email recipients.
     */
    public searchEmailRecipients(params: SearchEmailRecipientsParams): Promise<EmailRecipientItem[]> {
        return this.datatask.Post(1120, params);
    }

    /**
     * Retrieves list of email templates.
     *
     * @datatask 2901
     * @datataskName sp_ws_p2_mailtemplate_select_list
     * @param params the params for filtering the email templates lsit.
     * @returns a promise containing list of email templates.
     */
    public getEmailTemplates(params: EmailTemplatesParams): Promise<Item[]> {
        return this.datatask.Post(2901, params);
    }

    /**
     * Retrieves list of reports related to the given params.
     *
     * @datatask 899
     * @datataskName sp_ws_p2_selectreport_attach_list
     * @param params the params for the reports to retrieve.
     * @returns a promise containing reports for the given params.
     */
    public getReports(params: EmailCreateParams): Promise<ReportItem[]> {
        return this.datatask.Post(899, params);
    }

    /**
     * Retrieves Sirv images related to the email context.
     *
     * @datatask 4039
     * @param params the email context used to collect the images.
     * @returns a promise containing the related Sirv images.
     */
    public getPictureBanks(params: EmailCreateParams): Promise<EmailCreatePictureBankItem[]> {
        return this.datatask.Post(4039, params);
    }
}

export interface EmailCreatePictureBankItem {
    picturebank_keyno: string;
    full_url: string;
    argtype: string;
    argvalue: string;
}

export interface EmailCreateParams {
    argtype: string;
    argvalue: string;
    webpage: string;
    emailtemplate_keyno?: string;
    replyAll?: string;
}

export interface EmailCreateGet {
    recipient_email: string;
    email_heading: string;
    email_body: string;
    webpage: string;
    argtype: string;
    argvalue: string;
    emailcreate_heading: string;
    cust_no: string;
    customer_filtervalue: string;
    order_filtervalue: string;
    product_filtervalue: string;
    email_signature: string;
    portal_user_keyno: string;
    language_id: string;
    copy_email: string;
    bcc_email: string;
    cust_name: string;
    from_email: string;
    show_setting: '1' | '0';
    show_tab_cust_no_documents: '1' | '0';
    show_tab_prod_id_documents: '1' | '0';
    show_tab_custact_no_documents: '1' | '0';
    show_tab_argtype_documents: '1' | '0';
    setting_show_tab_external_documents: '1' | '0';
    errorcode: string;
    errormessage: string;
    businessco_no: string;
    emailtemplate_keyno: string;
    after_create_email_goto_orderheads: '1' | '0';
    custact_no: string;
}

export interface EmailCreateBeforeGet {
    previous_class: string;
    emails_to_add_to: string;
    emails_to_add_copy: string;
    emails_to_add_bcc: string;
}

export interface SearchEmailRecipientsParams {
    searchtext: string;
}

export interface EmailRecipientItem extends Item {
    email: string;
}

export interface EmailTemplatesParams {
    webpage: string;
}

export interface ReportItem {
    item_id: `${number}`;
    item_name: string;
    item_path: string;
    item_state: string;
    item_parms: string;
    orderby: `${number}`;
    report_id: string;
    item_glyphicon: string;
    item_glyphicon_2: string;
    item_is_selected: '1' | '0';
    item_glyphicon_color: string;
    item_glyphicon_color_2: string;
    p2_reportdef_keyno: `${number}`;
    report_type_keyno: `${number}`;
    htmlreport_keyno: `${number}`;
}

export interface EmailCreateSendParams {
    /**
     * Semicolon seperated string over recipients to send email to.
     */
    recipient_email: string;

    /**
     * Semicolon seperated string over recipients to send a copy to.
     */
    copy_email: string;

    /**
     * Semicolon seperated string over recipients to send a blind copy to.
     */
    bcc_email: string;

    /**
     * The senders email.
     */
    from_email: string;

    /**
     * Needs to be base65 encoded
     */
    email_heading: string;

    /**
     * Needs to be base65 encoded
     */
    email_body: string;
    webpage: string;
    argtype: string;
    argvalue: string;
    reports: EmailCreateSendAttachmentItem[];
    documents: (EmailCreateSendAttachmentItem | EmailCreateSendExternalAttachmentItem)[];

    /**
     * Whether to send by mgt-integration in frontend `'1'` or smtp from backend `'0'`
     */
    send_by_client: '1' | '0';
    emailtemplate_keyno: string;
}

export interface EmailCreateSendAttachmentItem {
    is_external: '0';
    docarc_keyno: `${number}` | number;
    docfilename: string;
}

export interface EmailCreateSendExternalAttachmentItem {
    is_external: '1';
    docarc_keyno: `${number}` | number;
    docfilename: string;
}

export interface EmailCreateClientSend {
    /**
     * The senders email.
     */
    from: string;

    /**
     * Semicolon seperated string of adresses to send to.
     */
    to: string;

    /**
     * Semicolon seperated string of adresses to send blind copy to.
     */
    bcc: string;

    /**
     * Semicolon seperated string of adresses to send copy to.
     */
    cc: string;

    /**
     * Base 64 encoded text of the emails subject.
     */

    /**
     * Base 64 encoded text of the emails subject.
     */
    subject: string;

    bodyText: string;

    /**
     * Base 64 encoded text of the emails content.
     */
    bodyHtml: string;

    /**
     * List of attachments to add to the email.
     */
    attachments: EmailCreateSendClientAttachmentItem[];
}

export interface EmailCreateSendClientAttachmentItem {
    docarcKeyno: `${number}` | number;
    fileName: string;
    contentBytes: string;
    contentType: string;
}
